import { formatClock } from '@krp/client-runtime'

import { formatMoment } from '../../../shared/model/query'
import type { AqsiFreshness, AqsiFreshnessCheck } from './types'

/**
 * Строка полосы «Источники» для кабинета AQSI (RP-SE13): готовые слова. Форма совпадает с `SourceFreshnessVm` пакета Platform по
 * структуре - пакеты друг друга не импортируют, binding соединяет их.
 */
export interface AqsiSourceLine {
  id: string
  name: string
  kindTitle: string
  productsText: string
  statusText: string
  kind: 'ok' | 'wait' | 'error' | 'idle'
  busy: boolean
  note: string | null
}

/** «1 товар», «2 товара», «5 товаров». */
export function productsText(count: number): string {
  if (count === 0) return 'товаров пока нет'
  const last = count % 10
  const tens = count % 100
  const word = tens >= 11 && tens <= 14 ? 'товаров' : last === 1 ? 'товар' : last >= 2 && last <= 4 ? 'товара' : 'товаров'
  return `${count} ${word}`
}

/** «14:32» общей утилитой времени (RP-UI-LINK); нечитаемый момент — как есть, пустой — словами. */
function clock(moment: string | null): string {
  if (moment === null) return 'время неизвестно'
  return formatClock(moment) ?? moment
}

/** Итог последней проверки словами: «Проверено 14:32, новых нет» / «новых 3, изменено 1» / «Ошибка: …». */
export function freshnessStatus(freshness: AqsiFreshness | null): { text: string; kind: AqsiSourceLine['kind'] } {
  if (freshness === null) return { text: 'Состояние не получено', kind: 'idle' }
  if (freshness.running) return { text: 'Идёт проверка…', kind: 'wait' }
  const at = clock(freshness.lastCheckedAt)
  switch (freshness.outcome) {
    case 'never':
      return {
        text:
          freshness.snapshotAt === null
            ? 'Снимок ещё не загружен'
            : `Снимок от ${formatMoment(freshness.snapshotAt)}; проверок после запуска службы ещё не было`,
        kind: 'idle',
      }
    case 'unchanged':
      return { text: `Проверено ${at}, новых нет`, kind: 'ok' }
    case 'changed':
      return { text: `Проверено ${at}, ${changes(freshness)}`, kind: 'ok' }
    case 'full':
      return { text: `Полная сверка ${at}: ${changes(freshness)}`, kind: 'ok' }
    case 'failed':
      return { text: `Ошибка: ${freshness.errorMessage ?? 'причина не передана'}`, kind: 'error' }
  }
}

function changes(freshness: AqsiFreshness): string {
  const parts = [`новых ${freshness.added}`]
  if (freshness.changed > 0) parts.push(`изменено ${freshness.changed}`)
  if (freshness.missing > 0) parts.push(`пропало ${freshness.missing}`)
  return parts.join(', ')
}

/** Пояснение к нажатию: проверка уже идёт или была только что - запроса в кабинет тогда не было, человек должен это увидеть словами. */
export function checkNote(last: AqsiFreshnessCheck | null, now: Date = new Date()): string | null {
  if (last === null) return null
  switch (last.status) {
    case 'already-running':
      return 'Проверка уже идёт'
    case 'throttled': {
      if (last.retryAfter === null) return 'Проверено только что'
      const seconds = Math.max(1, Math.ceil((new Date(last.retryAfter).getTime() - now.getTime()) / 1000))
      return seconds >= 60
        ? `Проверено только что - повторить можно через ${Math.ceil(seconds / 60)} мин`
        : `Проверено только что - повторить можно через ${seconds} с`
    }
    case 'failed':
    case 'done':
      return null
  }
}

/** Собирает строку полосы: источник, свежесть, итог последнего нажатия. */
export function aqsiSourceLine(
  source: { id: string; displayName: string },
  freshness: AqsiFreshness | null,
  checking: boolean,
  last: AqsiFreshnessCheck | null,
): AqsiSourceLine {
  const status = checking ? { text: 'Идёт проверка…', kind: 'wait' as const } : freshnessStatus(freshness)
  return {
    id: source.id,
    name: source.displayName,
    kindTitle: 'AQSI',
    productsText: freshness === null ? '' : productsText(freshness.products),
    statusText: status.text,
    kind: status.kind,
    busy: checking || freshness?.running === true,
    note: checking ? null : checkNote(last),
  }
}
